import { CreateInDialogButtonProps, FormDialogContext } from '@react-admin/ra-form-layout';
import { ReactElement } from 'react';
import { FileField, required, SelectInput, TextInput, useNotify, useRefresh } from 'react-admin';

import { DOCUMENTS_UPLOAD_LARGE_FILE_MAX_SIZE } from '@Common/constants/documents';
import {
  B2bCompanyDocumentType,
  PostB2bCompaniesDocumentsEndpoint,
  PostB2bCompanyDocumentRequest,
} from '@Common/types/bo/b2b/companies';

import {
  ActionModalFormWithToolbar,
  CreateInDialogButton,
  DialogToolbar,
  FileInput,
  SaveButton,
} from '@sh4res/bo-common/components';
import { companyDocumentsTypeChoices } from '@sh4res/bo-common/components/B2bCompanyDocuments/choices';
import { useUploadPresignedUrl } from '@sh4res/bo-common/hooks';
import { FileInputValue, getErrorMessage, Resources } from '@sh4res/bo-common/utils';

type CompanyDocumentsCreateValues = Pick<
  PostB2bCompanyDocumentRequest,
  'company_id' | 'document_comment' | 'task_id' | 'document_type'
> & {
  document_file?: FileInputValue;
};

export const B2bCompanyDocumentsCreate = ({
  record,
  icon,
  resource,
  documentTypes,
  ...props
}: CreateInDialogButtonProps['ButtonProps'] & {
  record?: Partial<PostB2bCompaniesDocumentsEndpoint['Client']['RequestBody']>;
  icon?: ReactElement;
  resource: Resources;
  documentTypes: B2bCompanyDocumentType[];
}) => {
  // Filter choices to only include types from the current category
  const typeChoices = companyDocumentsTypeChoices.filter((choice) =>
    documentTypes.includes(choice.id as B2bCompanyDocumentType),
  );
  const notify = useNotify();
  const refresh = useRefresh();
  const { upload } = useUploadPresignedUrl();

  const handleTransform = async (
    data: CompanyDocumentsCreateValues,
  ): Promise<PostB2bCompanyDocumentRequest> => {
    if (!data.document_file || !(data.document_file.rawFile instanceof File)) {
      throw new Error('Error during upload');
    }

    const uploadResult = await upload(data.document_file.rawFile);

    if (!uploadResult) {
      throw new Error('Error during upload');
    }

    return {
      company_id: data.company_id,
      document_type: data.document_type,
      document_comment: data.document_comment,
      presigned_path: uploadResult.path,
      document_name: data.document_file.title,
      task_id: data.task_id,
    };
  };

  const onSuccess = () => {
    notify('resources.bo_common.b2b.companies.documents.create.success', {
      type: 'success',
      messageArgs: { smart_count: 1 },
    });
    refresh();
  };

  const onError = (error: unknown) => {
    notify('resources.bo_common.b2b.companies.documents.create.failure', {
      type: 'warning',
      messageArgs: { error: getErrorMessage(error) },
      multiLine: true,
    });
  };

  return (
    <CreateInDialogButton
      testId="BoExternalB2bCompanyDocumentsCreate_CreateButton"
      resource={resource}
      fullWidth
      maxWidth="sm"
      title="resources.bo_common.b2b.companies.documents.create.title"
      ButtonProps={{
        variant: 'contained',
        color: 'info',
        size: 'small',
        sx: {
          fontWeight: 'bold',
          textTransform: 'none',
        },
        ...props,
      }}
      icon={icon}
      record={record}
      transform={handleTransform}
      mutationOptions={{ onSuccess, onError }}>
      <ActionModalFormWithToolbar
        toolbar={
          <DialogToolbar
            formDialogContext={FormDialogContext}
            button={
              <SaveButton
                color="primary"
                permissions={{
                  resource,
                  action: 'create',
                }}
                testId="BoExternalB2bCompanyDocumentsCreate_SaveButton"
              />
            }
          />
        }>
        <FileInput
          source="document_file"
          accept="application/pdf,image/png,image/jpeg,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          maxSize={DOCUMENTS_UPLOAD_LARGE_FILE_MAX_SIZE}
          validate={required()}
          data-testid="BoExternalB2bCompanyDocumentsCreate_DocumentFileInput">
          <FileField
            source="src"
            title="title"
            data-testid="BoExternalB2bCompanyDocumentsCreate_DocumentFileField"
          />
        </FileInput>
        <SelectInput
          source="document_type"
          choices={typeChoices}
          validate={required()}
          label="resources.bo_common.b2b.documents.fields.type"
          data-testid="BoExternalB2bCompanyDocumentsCreate_DocumentTypeInput"
        />
        <TextInput
          source="document_comment"
          defaultValue={null}
          data-testid="BoExternalB2bCompanyDocumentsCreate_DocumentCommentInput"
        />
      </ActionModalFormWithToolbar>
    </CreateInDialogButton>
  );
};
